import React, { useEffect } from 'react';

import { BottomActions, Box, Button, Stack } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ReactHookFormSelect2 } from 'common/components/form';
import ShowErrors from 'common/components/ShowErrors';
import { initProfileConfigurationActions } from 'common/utils/multiregion';
import { getUserSettings } from 'common/utils/session';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import * as selectors from '../selectors';
import validate from '../validate';

const FormContainer = ({
    errors,
    fetchLanguages,
    handleClickSave,
    initializeMultiregionForm,
    initialValues,
    isFetchingLanguages,
    isSaving,
    languages,
    updateConfiguration,
}) => {
    const { i18n, t } = useTranslation();

    const { control, handleSubmit, reset } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });
    useEffect(() => reset(initialValues), [initialValues]);

    useEffect(() => {
        const userSettings = getUserSettings();
        initializeMultiregionForm({ language: userSettings?.language?.key });
    }, []);

    useEffect(() => {
        fetchLanguages();
    }, [i18n.language]);

    const handleOnSubmit = (values) => {
        handleClickSave({ ...values }, (isLanguageChanged, newLanguage) => {
            if (isLanguageChanged) {
                i18n.changeLanguage(newLanguage);
            }

            initProfileConfigurationActions(updateConfiguration);
        });
    };

    return (
        <Stack component="form" onSubmit={handleSubmit(handleOnSubmit)}>
            <ReactHookFormSelect2
                control={control}
                isLoading={isFetchingLanguages}
                isClearable={false}
                label={t('Language')}
                name="language"
                options={[{ label: '', options: languages }]}
            />

            <ShowErrors errors={errors} />

            <BottomActions>
                <Box mt={2}>
                    <Button type="submit" disabled={isSaving}>
                        {t('Save')}
                    </Button>
                </Box>
            </BottomActions>
        </Stack>
    );
};

const mapStateToProps = createStructuredSelector({
    languages: selectors.getLanguagesOptions,
    isFetchingLanguages: selectors.getIsFetchingLanguages,
    timezones: selectors.getTimezonesOptions,
    isFetchingTimezones: selectors.getIsFetchingTimezones,
    isSaving: selectors.getIsSaving,
    errors: selectors.getErrorsSave,
    initialValues: selectors.getInitialValues,
});

const mapDispatchToProps = (dispatch) => ({
    fetchLanguages: () => dispatch(actions.fetchLanguages()),
    fetchTimezones: () => dispatch(actions.fetchTimezones()),
    handleClickSave: (values, onSuccessCallback) =>
        dispatch(actions.save(values, onSuccessCallback)),
    initializeMultiregionForm: (data) =>
        dispatch(actions.initializeMultiregionForm(data)),
    updateConfiguration: (data) => dispatch(actions.updateConfiguration(data)),
});

FormContainer.propTypes = {
    errors: PropTypes.array,
    fetchLanguages: PropTypes.func,
    handleClickSave: PropTypes.func,
    initializeMultiregionForm: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingLanguages: PropTypes.bool,
    isSaving: PropTypes.bool,
    languages: PropTypes.array,
    updateConfiguration: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
